import React from 'react';
import gql from 'graphql-tag';
import I18n from 'i18n-js';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { Paths } from './shared/url_helpers';
import {
  CoreCheckoutDeleteCreditCard,
  CreditCardDeleteButtonFields,
  core_apimessages_CheckoutPaymentMethod_Type as PaymentMethodType,
} from '../schemas/rql';
import { MutationFn } from 'react-apollo';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { RCButton, RCAlertBox } from '@reverbdotcom/cadence/components';
import { withRouter, WithRouterProps } from 'react-router';
import { CreditCardWalletClickInteraction } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useTrackCheckoutClickEvent } from '../lib/checkoutTracking';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { connect } from '@reverbdotcom/commons/src/connect';

type CreditCard = CreditCardDeleteButtonFields.CreditCards;

interface MutationProps {
  deleteCheckoutCreditCard: DeleteCheckoutCreditCardMutationFn;
}

interface ExternalProps {
  creditCard: CreditCard;
}

type Props = ExternalProps & MutationProps & WithRouterProps;

type DeleteCheckoutCreditCardMutationFn = MutationFn<CoreCheckoutDeleteCreditCard.Mutation, CoreCheckoutDeleteCreditCard.Variables>;

const CREDIT_CARD_TROUBLESHOOTING_TIPS_ARTICLE_ID = '1500005385482';

export function CreditCardDeleteButton(props: Props) {
  const selectedCardId = props.creditCard.id;
  const checkoutId = props.params.id;

  const checkoutClickEvent = useTrackCheckoutClickEvent({
    id: checkoutId,
    paymentMethod: {
      type: PaymentMethodType.DIRECT_CHECKOUT,
    },
  });

  const { onDelete, loading, errors } = useDeleteCardCallback(
    selectedCardId,
    props.deleteCheckoutCreditCard,
  );

  function useDeleteCardCallback(
    selectedCardId: string,
    deleteCheckoutCreditCard: DeleteCheckoutCreditCardMutationFn,
  ) {
    const [mutate, { loading, errors }] = useMutation(deleteCheckoutCreditCard);

    async function onDelete() {
      const result = await mutate({
        variables: {
          input: {
            id: selectedCardId,
          },
        },
        refetchQueries: ['Core_CreditCardWallet_Index', 'Core_Checkout_Layout'],
        awaitRefetchQueries: true,
      });

      if (result) {
        const creditCardWalletIndexPath = Paths.checkoutCreditCardWallet.expand({
          checkoutId,
        });
        checkoutClickEvent(CreditCardWalletClickInteraction.DeleteCard);
        props.router.push(creditCardWalletIndexPath);
      }
    }

    return { onDelete, loading, errors };
  }

  const troubleshootingUrl = useHelpCenterArticleUrl(CREDIT_CARD_TROUBLESHOOTING_TIPS_ARTICLE_ID);

  return (
    <div className="mt-2">
      {errors.length > 0 && (
        <div className="mb-2">
          <RCAlertBox type="error">
            {errors.map(e => <li key={`${e.field}-${e.message}`}>{e.message}</li>)}
            <div className="mt-1">
              <a href={troubleshootingUrl}>
                {I18n.t('discovery.creditCardWallet.cardTroubleshooting')}
              </a>
            </div>
          </RCAlertBox>
        </div>
      )}
      <RCButton
        variant="primary"
        theme="danger"
        fullWidth
        disabled={loading}
        onClick={onDelete}
      >
        {loading ? translate('loading') : translate('default')}
      </RCButton>
    </div>
  );
}

function translate(key: string) {
  return I18n.t(`discovery.checkout.creditCardDeleteButton.${key}`);
}

export const CreditCardDeleteButtonFragment = gql`
  fragment CreditCardDeleteButtonFields on rql_Me {
    creditCards {
      id
    }
  }
`;

const withMutation = withGraphql<Props, CoreCheckoutDeleteCreditCard.Mutation, CoreCheckoutDeleteCreditCard.Variables>(
  gql`
  mutation Core_Checkout_DeleteCreditCard(
    $input: Input_core_apimessages_DeleteMyCreditCardRequest
  ) {
    deleteMyCreditCard(input: $input) {
      id
    }
  }
  `,
  {
    name: 'deleteCheckoutCreditCard',
  },
);

export default connect<ExternalProps>([
  withMutation,
  withRouter,
])(CreditCardDeleteButton);
